import { useAtom } from 'jotai';
import difference from 'lodash/difference';
import * as React from 'react';

import * as icons from '@creditornot/alchemy-react/icons';
import { TextButton } from '@creditornot/cb-button';
import { Popover } from '@creditornot/cb-popover';
import { HUIPPUKIVA_TENANT_ID } from 'common/constants';
import { useWarpMerchant } from 'common/hooks/useWarpMerchant';
import { selectedCountryCodesAtom } from 'common/state/countryState';

import { CountryPopover } from './CountryPopover';
import { useCountriesQuery } from '../../../hooks/useCountries';
import { useFormatMessage } from '../../../hooks/useFormatMessage';
import { LocalizedCountry } from '../LocalizedCountry';

type Props = {
  onChange?: (countryCodes: string[]) => void;
};

export const CountrySelector = ({ onChange }: Props): React.ReactElement => {
  const [countryCodes, setCountryCodes] = useAtom(selectedCountryCodesAtom);
  const { data: countries, isLoading, isError } = useCountriesQuery();
  const formatMessage = useFormatMessage();
  const { getMerchant } = useWarpMerchant();
  const merchant = getMerchant();
  const merchantId = merchant?.id || '';

  // ensure the current countries returned from wm-director and the countryCodes saved in local storage are in sync
  React.useEffect(() => {
    if (!countries?.length) return;
    const validCodes = countries.map((c) => c.countryCode);
    const invalidCodes = countryCodes.filter((code) => !validCodes.includes(code));
    if (invalidCodes.length > 0) {
      setCountryCodes(difference(countryCodes, invalidCodes));
    }
  }, [countryCodes, countries, setCountryCodes]);

  return (
    <Popover
      offset={4}
      placement="bottom-start"
      renderArrow={false}
      content={({ close }) => (
        <CountryPopover
          countries={countries ?? []}
          isLoading={isLoading}
          isError={isError}
          selected={countryCodes}
          onChange={(selected) => {
            setCountryCodes(selected);
            onChange?.(selected);
          }}
          onClose={close}
        />
      )}
      zIndex={10}
    >
      {({ refProps }) => (
        <TextButton
          {...refProps}
          variant={countryCodes.length > 0 ? 'brand' : 'lightGrey'}
          size="small"
          icon={icons.chevronDown}
          iconPosition="end"
          data-test-id="select-country-button"
          disabled={merchantId === HUIPPUKIVA_TENANT_ID}
        >
          {countryCodes.length === 0 && formatMessage('components.country_selector.select_country')}
          {countryCodes.length === 1 && <LocalizedCountry countryCode={countryCodes[0]} />}
          {countryCodes.length > 1 &&
            formatMessage('components.country_selector.countries', {
              count: countryCodes.length,
            })}
        </TextButton>
      )}
    </Popover>
  );
};
